import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import ProductView from '../ProductView/ProductView';
import ProductService from '../../../services/ProductService';
import Info from '../Alerts/Info';
import { ShopContext } from '../../../contexts/ShopContext';
import { Skeleton } from '@mui/material';
import { SHOP_MODE, PRODUCT_TYPES } from '../../../const';
import CartHelper from '../../../helpers/CartHelper';

const styles = {
  productsListParent: {
    minHeight: 100,
    position: 'relative',
  },
  skeletonCard: {
    padding: '10px',
    borderRadius: 12,
    background: '#fff',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minHeight: 160,
  },
  skeletonImage: {
    width: '100%',
    height: 100,
    borderRadius: 10,
  },
  skeletonTitle: {
    height: 16,
    width: '60%',
    borderRadius: 6,
  },
  skeletonPrice: {
    height: 14,
    width: '30%',
    borderRadius: 6,
  },
};

export default function ProductListView({
  selectedCategory,
  searchKey,
  fnOnResultChange,
  fnOpenProductDetailsPage,
  showActionSection = true,
}) {
  
  const { shop } = useContext(ShopContext);
  const [products, setProducts] = useState(null);
  const [isLoading, setLoading] = useState(!selectedCategory);

  const loadProducts = useCallback(
    async (selectedCategory, searchKey) => {
      try {
        if (!selectedCategory && !shop?.meta?.allCategoryRequired && !searchKey) {
          return;
        }
        setLoading(true);
        const category = searchKey ? null : selectedCategory;
        const products = await ProductService.getProducts(shop, category, searchKey);
        products.sort((a, b) => {
          const aOutOfStock = a.type !== PRODUCT_TYPES.SERVICE && a.stock <= 0;
          const bOutOfStock = b.type !== PRODUCT_TYPES.SERVICE && b.stock <= 0;

          if (aOutOfStock && !bOutOfStock) {
            return 1;
          } else if (!aOutOfStock && bOutOfStock) {
            return -1;
          } else {
            return 0;
          }
        });

        setProducts(products);
        setLoading(false);

        fnOnResultChange?.(products.length);
      } catch (error) {
        console.error(error);
      }
    },
    [shop, fnOnResultChange]
  );

  useEffect(() => {
    setProducts(null);
    loadProducts(selectedCategory, searchKey);
  }, [selectedCategory, searchKey, loadProducts]);

  const getQuantityForProduct = (productId) => {
    const cart = CartHelper.getCart(shop);
    let total = 0;

    Object.keys(cart.products).forEach((key) => {
      if (key.startsWith(productId.toString())) {
        total += cart.products[key].quantity;
      }
    });
    return total;
  };

  return (
    <div style={styles.productsListParent}>
      {isLoading ? (
        <Grid container spacing={1}>
          {[...Array(8)].map((_, idx) => (
            <Grid item xs={6} sm={4} md={2} key={idx}>
              <div style={styles.skeletonCard}>
                <Skeleton variant="rectangular" animation="wave" style={styles.skeletonImage} />
                <Skeleton variant="text" animation="wave" style={styles.skeletonTitle} />
                <Skeleton variant="text" animation="wave" style={styles.skeletonPrice} />
              </div>
            </Grid>
          ))}
        </Grid>
      ) : products == null ? (
        <div style={{ padding: '10px', fontWeight: 'bold', color: '#555' }}>Products not available yet.</div>
      ) : products.length ? (
        <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="stretch">
          {products.map((product) => (
            <ProductView
              key={product.id}
              category={selectedCategory}
              product={product}
              quantityInCart={getQuantityForProduct(product.id)}
              fnOpenProductDetailsPage={fnOpenProductDetailsPage}
              showDetailsPage={true}
              showStatus={true}
              showActionSection={showActionSection && shop?.mode !== SHOP_MODE.VIEW_ONLY}
            />
          ))}
        </Grid>
      ) : (
        <Info title={'No Products'} content={'No products available in this category.'} />
      )}
    </div>
  );
}
